import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Field } from 'react-final-form';
import PropTypes from 'prop-types';
import { Divider, InputAdornment, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import DatePickerFieldAdapter from 'components/shared/field-adapters/DatePickerFieldAdapter';
import SelectFieldAdapter from 'components/shared/field-adapters/SelectFieldAdapter';
import TextAreaFieldAdapter from 'components/shared/field-adapters/TextAreaFieldAdapter';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import TypeaheadFieldAdapter from 'components/shared/field-adapters/TypeaheadFieldAdapter';
import { currencyOptions, getPaymentMethodOptions } from 'common/constants/options';
import requestStatus from 'common/constants/status';
import { isRequiredField } from 'common/utils/general';
import useForms from 'hooks/useForms';
import planSlice from 'store/modules/plan';

function ClientPaymentFields({
  clientOptions,
  onClientIdChange,
  onPlanChange,
  requiredFields,
  searchStatus,
  sessionsPaidHint = null
}) {
  const { t } = useTranslation(['labels']);
  const { clearClientSearchResults } = useForms;
  const dispatch = useDispatch();

  const planOptions = useSelector((state) => state.plan.planOptions);
  const planStatus = useSelector((state) => state.plan.status);

  // Cargar todos los planes disponibles al montar el componente
  useEffect(() => {
    dispatch(planSlice.actions.listAll());
  }, [dispatch]);

  return (
    <>
      <Grid container spacing={4}>
        <Grid xs={12}>
          <Field
            id="user_id"
            name="user_id"
            component={TypeaheadFieldAdapter}
            fullWidth
            loading={searchStatus === requestStatus.loading}
            isMultiple={false}
            onClose={clearClientSearchResults}
            onInputChange={onClientIdChange}
            options={clientOptions}
            renderInputProps={{
              label: t('client', { count: 1, ns: 'labels' }),
              required: isRequiredField('user_id', requiredFields)
            }}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="plan_id"
            name="plan_id"
            component={SelectFieldAdapter}
            options={planOptions}
            label={t('plan', { ns: 'labels' })}
            onChange={(event, value) => {
              if (value && onPlanChange) {
                onPlanChange(value);
              }
            }}
            fullWidth
            required={isRequiredField('plan_id', requiredFields)}
            disabled={planStatus === requestStatus.loading}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="price"
            name="price"
            component={TextFieldAdapter}
            label={t('price', { ns: 'labels' })}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            fullWidth
            required={isRequiredField('price', requiredFields)}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="currency"
            name="currency"
            component={SelectFieldAdapter}
            options={currencyOptions}
            label={t('currency', { ns: 'labels' })}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="sessions_paid"
            name="sessions_paid"
            component={TextFieldAdapter}
            label={t('sessionsPaid', { ns: 'labels' })}
            fullWidth
            required={isRequiredField('sessions_paid', requiredFields)}
          />
          {/* Solo al crear: el arrastre de sesiones lo suma el backend, el admin no debe sumarlo. */}
          {sessionsPaidHint && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
              {sessionsPaidHint}
            </Typography>
          )}
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="start_date"
            name="start_date"
            component={DatePickerFieldAdapter}
            label={t('startDate', { ns: 'labels' })}
            renderInputProps={{
              fullWidth: true,
              required: isRequiredField('start_date', requiredFields)
            }}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="end_date"
            name="end_date"
            component={DatePickerFieldAdapter}
            label={t('endDate', { ns: 'labels' })}
            renderInputProps={{
              fullWidth: true,
              required: isRequiredField('end_date', requiredFields)
            }}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="created_at"
            name="created_at"
            component={DatePickerFieldAdapter}
            label={t('createdAt', { ns: 'labels' })}
            renderInputProps={{
              fullWidth: true,
              required: isRequiredField('created_at', requiredFields)
            }}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="payment_method"
            name="payment_method"
            component={SelectFieldAdapter}
            options={getPaymentMethodOptions(t)}
            label={t('paymentMethod', { ns: 'labels' })}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="invoice_url"
            name="invoice_url"
            component={TextFieldAdapter}
            label={t('invoice', { ns: 'labels' })}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="gateway_transaction_id"
            name="gateway_transaction_id"
            component={TextFieldAdapter}
            label={t('gatewayTransactionId', { ns: 'labels' })}
            fullWidth
            disabled
          />
        </Grid>
      </Grid>
      <Typography
        component="h4"
        sx={{
          fontSize: (theme) => theme.typography.pxToRem(26),
          marginBottom: (theme) => theme.spacing(1),
          marginTop: '35px'
        }}>
        {t('observations', { ns: 'labels' })}
      </Typography>
      <Divider sx={{ marginBottom: '22px' }} />
      <Grid container>
        <Grid xs={12}>
          <Field
            id="observations"
            name="observations"
            component={TextAreaFieldAdapter}
            label={t('observations', { ns: 'labels' })}
            helperText={t('text', { ns: 'validations' })}
            multiline
            minRows={5}
            type="text"
            fullWidth
          />
        </Grid>
      </Grid>
    </>
  );
}

ClientPaymentFields.propTypes = {
  clientOptions: PropTypes.array.isRequired,
  onClientIdChange: PropTypes.func.isRequired,
  onPlanChange: PropTypes.func.isRequired,
  requiredFields: PropTypes.array.isRequired,
  searchStatus: PropTypes.string.isRequired,
  sessionsPaidHint: PropTypes.string
};

export default ClientPaymentFields;
